import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Captions, CaptionsOff, Play, RotateCcw, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { BrandLogo } from "@/components/BrandLogo";

const SRC = "https://customer-assets-4nw71qhi.emergentagent.net/wingman/ac664a3f-91c8-4d23-85de-81822e60d0df/attachments/e395ae6168674515850298abe4beea74_the-climb-v3.mp4";
const ctaCls = "inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-semibold text-black transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#F3C649] sm:px-6 sm:py-3";

const EndPrompt = ({ onCta, ctaTo, replay }) => (
  <div className="fade-up absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/75 p-4 text-center backdrop-blur-md sm:gap-5" data-testid="video-end-prompt">
    <p className="hidden font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] sm:block">You don't have to climb alone</p>
    <h3 className="font-display text-lg font-semibold text-zinc-100 sm:text-3xl">Ready for your own ascent?</h3>
    {onCta ? <button type="button" onClick={onCta} className={ctaCls} data-testid="video-end-cta"><Sparkles className="h-4 w-4" />Start your free Elite trial</button>
      : <Link to={ctaTo} className={ctaCls} data-testid="video-end-cta"><Sparkles className="h-4 w-4" />Start your free Elite trial</Link>}
    <span className="text-[11px] text-zinc-400 sm:text-xs">7 days free · No card required</span>
    <button type="button" onClick={replay} className="inline-flex items-center gap-1.5 text-xs text-zinc-300 transition-colors hover:text-[#F3C649]" data-testid="video-replay-button"><RotateCcw className="h-3.5 w-3.5" />Watch again</button>
  </div>
);

export const FilmPlayer = ({ onCta, ctaTo = "/login", compact = false }) => {
  const ref = useRef(null);
  const [state, setState] = useState("idle");
  const [cc, setCc] = useState(true);
  useEffect(() => { const t = ref.current?.textTracks?.[0]; if (t) t.mode = cc ? "showing" : "hidden"; }, [cc, state]);
  const play = () => { setState("playing"); requestAnimationFrame(() => { const v = ref.current; if (!v) return; v.currentTime = 0; v.play().catch(() => {}); }); };
  return (
    <div className="group relative rounded-[1.25rem] bg-gradient-to-br from-[#D4AF37]/60 via-[#27272A] to-[#D4AF37]/30 p-px shadow-[0_30px_120px_-30px_rgba(212,175,55,0.35)]">
      <div className="relative aspect-video overflow-hidden rounded-[1.2rem] bg-black">
        <video ref={ref} src={SRC} poster="/video-poster.jpg" controls={state === "playing"} playsInline preload="none"
          className="h-full w-full object-cover" onEnded={() => setState("ended")} data-testid="showcase-video">
          <track kind="captions" src="/the-climb.en.vtt" srcLang="en" label="English" default />
        </video>
        {state === "idle" && (
          <button type="button" onClick={play} aria-label="Play video" data-testid="showcase-play-button"
            className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/70 via-black/20 to-transparent">
            <span className={cn("relative flex items-center justify-center rounded-full bg-[#D4AF37] text-black shadow-[0_0_0_10px_rgba(212,175,55,0.18)] transition-transform duration-300 group-hover:scale-110", compact ? "h-14 w-14" : "h-16 w-16 sm:h-20 sm:w-20")}>
              <span className="absolute inset-0 animate-ping rounded-full bg-[#D4AF37]/40" />
              <Play className="relative ml-1 h-7 w-7 fill-black" />
            </span>
            <span className="absolute bottom-3 left-3 rounded-full border border-white/15 bg-black/50 px-3 py-1 font-mono text-[11px] text-zinc-200 backdrop-blur-md sm:bottom-5 sm:left-5">▶ 1:00 · Captions on</span>
          </button>
        )}
        {state === "playing" && (
          <button type="button" onClick={() => setCc(!cc)} aria-label={cc ? "Hide captions" : "Show captions"} aria-pressed={cc} data-testid="captions-toggle"
            className={cn("absolute right-3 top-3 flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs backdrop-blur-md transition-colors", cc ? "border-[#D4AF37]/60 bg-black/60 text-[#F3C649]" : "border-white/20 bg-black/40 text-zinc-300")}>
            {cc ? <Captions className="h-4 w-4" /> : <CaptionsOff className="h-4 w-4" />}CC
          </button>
        )}
        {state === "ended" && <EndPrompt onCta={onCta} ctaTo={ctaTo} replay={play} />}
      </div>
    </div>
  );
};

export const VideoShowcase = ({ onCta }) => (
  <section id="video-showcase" className="relative z-[2] mx-auto max-w-6xl scroll-mt-24 px-4 pb-20 sm:px-6 lg:px-12" data-testid="video-showcase">
    <div className="mb-6 flex flex-col gap-2 sm:mb-8 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="mb-2 font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]">The Climb · 1 min</p>
        <h2 className="max-w-3xl font-display text-2xl font-semibold leading-tight tracking-tight text-zinc-100 sm:text-3xl" data-testid="video-showcase-heading">Job searching today feels like climbing a mountain with the wrong gear. <span className="gold-text">RPOPRO gives you the right tools — and a guide.</span></h2>
      </div>
      <p className="max-w-sm text-sm text-zinc-500" data-testid="video-showcase-trust">7 Day Free Trial - Cancel Any Time - Delete Your Data Any Time - We do not sell your information.</p>
    </div>
    <FilmPlayer onCta={onCta} />
  </section>
);

export const MadeWithRpopro = () => (
  <section className="fade-up mt-10 rounded-2xl border border-[#27272A] bg-[#18181B]/60 p-4 backdrop-blur-xl sm:p-6" data-testid="made-with-rpopro">
    <div className="mb-4 flex items-center justify-between gap-3">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Made with RPOPRO</p>
        <h3 className="mt-1 font-display text-base text-zinc-100 md:text-lg">The Climb — the story behind this profile</h3>
      </div>
      <BrandLogo size="sm" className="hidden opacity-90 sm:inline-flex" />
    </div>
    <FilmPlayer compact />
  </section>
);
